import { HttpClient } from "@angular/common/http";
import {inject, Injectable} from "@angular/core";
import { environment } from "src/environments/environment";
import jwt_decode from "jwt-decode";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { Router } from "@angular/router";
import { SideNavService } from "../components/nav-bar/Services/side-nav.service";
import { RootRoutePaths } from "../models/routes-constants";
import { APIResponse } from "../models/Response";
import { LoginResponse } from "../modules/shared/Models/login-model.model";
import {getMessaging} from "firebase/messaging";
import {deleteToken} from "@firebase/messaging";
import {SecureStorageService} from "../modules/core/services/secure-storage.service";
import {LocalStorageRelatedData} from "../local-storage-related-data.model";

@Injectable({
    providedIn: "root",
})
export class LoginService {
    apiServerUrl = environment.apiBaseUrl;

    private isUserLoggedIn: BehaviorSubject<boolean | null> = new BehaviorSubject<any>(null);
    isUserLoggedIn$ = this.isUserLoggedIn.asObservable();
    private secureStorageService = inject(SecureStorageService);
    private isLoggingOut = false;

    constructor(
        private http: HttpClient,
        private router: Router,
        private sideNavService: SideNavService,
    ) {}

    //  to login the user
    doLogin(credentials: any): Observable<APIResponse<LoginResponse>> {
        return this.http.post<APIResponse<LoginResponse>>(`${this.apiServerUrl}/auth/login`, credentials).pipe(
            catchError((error) => {
                return throwError(error);
            }),
        );
    }

    isLoggedIn() {
        let token = this.getToken();
        let isSignUpCompleted = this.secureStorageService.getItem(LocalStorageRelatedData.IS_SIGN_UP_COMPLETE);
        return !(token == undefined || token == "" || token == null) && isSignUpCompleted == true;
    }

    logout(): Observable<any> {
        return this.http.post(`${this.apiServerUrl}/user/logout`, null).pipe(
            catchError((error) => {
                return throwError(error);
            }),
        );
    }

    getToken() {
        return this.secureStorageService.getItem<string>(LocalStorageRelatedData.TOKEN);
    }

    //  to send the otp to user
    doSendOtp(sendotp: any): Observable<any> {
        return this.http.post(`${this.apiServerUrl}/auth/sendotp`, sendotp).pipe(
            catchError((error) => {
                console.log("Inside error blok");
                return throwError(error);
            }),
        );
    }

    //  to decode the token
    getDecodedAccessToken(token: string): any {
        try {
            return jwt_decode(token);
        } catch (Error) {
            return null;
        }
    }

    getTokenAccountIds(decodedToken: any): any {
        return decodedToken?.acountIds;
    }

    getTokenEmail(decodedToken: any): string {
        return decodedToken.sub;
    }

    getUserEmailFromToken(): string {
        let token: any = this.getToken();
        let decodedToken: any = this.getDecodedAccessToken(token);
        return this.getTokenEmail(decodedToken);
    }

    getCreatedDateTimeOfUserFromLocalStorage(): string {
        return this.secureStorageService.getItem<string>(LocalStorageRelatedData.CREATED_DATE_TIME) || "";
    }

    // see user created date time of user is older than 30 days
    isUserAccountIsNew() {
        const timeInMillis = this.getCreatedDateTimeOfUserFromLocalStorage();
        if (!timeInMillis) {
            return false;
        } else {
            let dateTime = new Date(Number(timeInMillis));
            let currentDateTime = new Date();
            let diffInTime = currentDateTime.getTime() - dateTime.getTime();
            let diffInDays = diffInTime / (1000 * 3600 * 24);
            return diffInDays < 30;
        }
    }

    public setUserDetailsOnLogin(
        firstName: string,
        lastName: string,
        userId: number,
        sendOtpUserName: string,
        createdDateTimeOfUser: string | number,
    ) {
        this.secureStorageService.setItem(LocalStorageRelatedData.FIRST_NAME , firstName);
        this.secureStorageService.setItem(LocalStorageRelatedData.LAST_NAME , lastName);
        this.secureStorageService.setItem(LocalStorageRelatedData.USER_NAME , sendOtpUserName);
        this.secureStorageService.setItem(LocalStorageRelatedData.LOGGED_IN_USER_ID , userId);
        this.secureStorageService.setItem(LocalStorageRelatedData.CREATED_DATE_TIME , String(createdDateTimeOfUser));
    }

    public setDeviceId(deviceId: string) {
        this.secureStorageService.setItem(LocalStorageRelatedData.DEVICE_ID , deviceId);
    }

    userLogInOrLogoutEventEmitter(isUserLoggedIn: boolean) {
        this.isUserLoggedIn.next(isUserLoggedIn);
    }

    triggerLoginEvent() {
        this.secureStorageService.setItem(LocalStorageRelatedData.LOGGED_IN, true);
    }

    triggerLogoutEvent() {
        this.secureStorageService.setItem(LocalStorageRelatedData.LOGGED_OUT, true);
    }

    attemptToDeleteFCMToken(){
        if (getMessaging()) {
            deleteToken(getMessaging())
                .then(() => {
                    console.log("FCM Token deleted.");
                })
                .catch((error) => {
                    console.log("Error deleting FCM token", error);
                });
        } else {
            console.log("No FCM instance found.");
        }
    }

    isCurrentlyLoggingOut(): boolean {
        return this.isLoggingOut;
    }

    deleteUserLocalDataAndEmitEvents(redirectionRequired: boolean) {
        this.isLoggingOut = true;
        // close all mat dialogs in application
        console.log("logout called, deleting local data and emitting events");
        this.secureStorageService.clear();
        this.secureStorageService.clearSession();
        console.log("cleared local and session storage");
        this.sideNavService.close();
        this.userLogInOrLogoutEventEmitter(false);
        this.triggerLogoutEvent();
        this.attemptToDeleteFCMToken();
        if (redirectionRequired) {
            this.router
                .navigate([RootRoutePaths.HOME], { queryParams: { nc: new Date().getTime() } })
                .then(() => {
                    window.location.reload();
                })
                .catch(() => {
                    window.location.reload();
                });
        } else {
            window.location.reload();
        }
    }

    logoutTheUserAndRedirectToLogin() {
        this.logout().subscribe({});
        this.deleteUserLocalDataAndEmitEvents(true);
    }

    logoutTheUserButDontRedirectToLogin() {
        this.logout().subscribe({});
        this.deleteUserLocalDataAndEmitEvents(false);
    }

    saveTeamInviteId(inviteId: string) {
        this.secureStorageService.setItem(LocalStorageRelatedData.TEAM_INVITE_ID , inviteId);
    }

    getTeamInviteId() {
        return this.secureStorageService.getItem<string>(LocalStorageRelatedData.TEAM_INVITE_ID);
    }

    clearTeamInviteId() {
        this.secureStorageService.removeItem(LocalStorageRelatedData.TEAM_INVITE_ID);
    }
}
